import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import useAuth from "../helper/useAuth";
import ApprovedPartnerDashboard from "../components/ApprovedPartnerDashboard";
import ClientPlatformTab from "../components/ClientPlatformTab";
import { useTheme } from "../components/ThemeProvider";

// ---------------------------------------------------------------------------
// DemoPage
// ---------------------------------------------------------------------------
// Standalone "/demo" route that hosts everything we built for the team-lead
// presentation. Profile page is intentionally clean of this content so that
// regular partner-onboarding flows (apply, pending, rejected, approved) can
// evolve against real APIs without demo drift.
//
// URL params drive the demo state, exactly like the previous in-profile
// implementation:
//   ?previewPartner=approved        → Reseller dashboard (forced approved)
//   ?clientState=fresh|via-link|code-saved|has-account → Customer tab
// Without either, a placeholder is shown explaining what to click.
// ---------------------------------------------------------------------------

function normalizeUser(data) {
  if (!data || typeof data !== "object") return null;
  return data.user || data.data || data;
}

const CLIENT_STATE_VALUES = [
  "fresh",
  "via-link",
  "code-saved",
  "has-account",
];

const DemoPage = ({ openModal, setClientSetupContext } = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isDarkMode } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth gate — same as profile. Easy to relax later if marketing wants
  // an anonymous demo URL.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${i18n.language}`, { replace: true });
    }
  }, [isAuthenticated, i18n.language, navigate]);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    const apiLocale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.get(`/api/${apiLocale}/auth/getUserInfo`);
      setUser(normalizeUser(data));
    } catch (e) {
      // Fall back to a synthetic user so the demo still works if the API
      // is unavailable — the team-lead presentation must never go blank.
      setUser({
        id: 1,
        name: i18n.language === "hy" ? "Դեմո Օգտատեր" : "Demo User",
        email: "demo@nomio.am",
        phone_number: "+374 99 33 44 55",
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, i18n.language]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // ---- URL param state ------------------------------------------------------
  const previewPartner = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("previewPartner");
  }, [location.search]);

  const clientStateParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get("clientState");
    return CLIENT_STATE_VALUES.includes(v) ? v : null;
  }, [location.search]);

  const navigateWithParams = useCallback(
    (mutate) => {
      const params = new URLSearchParams(location.search);
      mutate(params);
      const qs = params.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
        replace: false,
      });
    },
    [location.pathname, location.search, navigate],
  );

  const enterResellerDemo = useCallback(() => {
    navigateWithParams((params) => {
      params.set("previewPartner", "approved");
      params.delete("clientState");
    });
  }, [navigateWithParams]);

  const enterCustomerDemo = useCallback(
    (state) => {
      navigateWithParams((params) => {
        params.delete("previewPartner");
        if (state && state !== "fresh") {
          params.set("clientState", state);
        } else {
          params.delete("clientState");
          // ensure we still mark "customer demo active" — use fresh state
          params.set("clientState", "fresh");
        }
      });
    },
    [navigateWithParams],
  );

  const exitDemo = useCallback(() => {
    navigateWithParams((params) => {
      params.delete("previewPartner");
      params.delete("clientState");
    });
  }, [navigateWithParams]);

  if (!isAuthenticated) return null;

  // Synthesize a forced-approved partner so ApprovedPartnerDashboard renders
  // with the user's id / name baked into the fake data set.
  const syntheticPartner =
    previewPartner === "approved"
      ? {
          id: user?.id || 1,
          mobile_user_id: user?.id || 1,
          partner_status: "approved",
        }
      : null;

  const showResellerDemo = previewPartner === "approved";
  const showCustomerDemo = Boolean(clientStateParam);
  const showPlaceholder = !showResellerDemo && !showCustomerDemo;

  return (
    <>
      <div
        className="cover-photo position-relative z-index-1 overflow-hidden"
        style={{
          minHeight: 220,
          background: "var(--main-gradient)",
        }}
      />

      <section
        className="profile-section"
        style={{ paddingBottom: "clamp(80px, 12vw, 180px)" }}
      >
        <div className="container container-two">
          <div className="profile-content-wrapper z-index-1 position-relative mt--100">
            <div className="d-flex flex-column gap-3">
              <DemoIntroCard isDarkMode={isDarkMode} />

              {!loading && (
                <DemoEntryHub
                  isDarkMode={isDarkMode}
                  previewPartner={previewPartner}
                  clientStateParam={clientStateParam}
                  onEnterReseller={enterResellerDemo}
                  onEnterCustomer={enterCustomerDemo}
                  onExit={exitDemo}
                />
              )}

              {!loading && showResellerDemo && (
                <ApprovedPartnerDashboard
                  partner={syntheticPartner}
                  user={user}
                />
              )}

              {!loading && showCustomerDemo && (
                <ClientPlatformTab
                  user={user}
                  isDarkMode={isDarkMode}
                  onOpenWizard={(initialContext) => {
                    if (typeof setClientSetupContext === "function") {
                      const [firstName, ...rest] = String(user?.name || "")
                        .trim()
                        .split(/\s+/);
                      setClientSetupContext({
                        firstName: firstName || "",
                        lastName: rest.join(" ") || "",
                        ...(initialContext || {}),
                      });
                    }
                    if (typeof openModal === "function") {
                      openModal("clientSetup");
                    }
                  }}
                />
              )}

              {!loading && showPlaceholder && (
                <DemoPlaceholder isDarkMode={isDarkMode} />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// ---------------------------------------------------------------------------
// DemoIntroCard — page header explaining what /demo is.
// ---------------------------------------------------------------------------
function DemoIntroCard({ isDarkMode }) {
  const { t } = useTranslation();
  const isVisuallyDark = !isDarkMode;
  return (
    <div
      className="rounded-4 p-4 text-white"
      style={{ background: "var(--main-gradient)" }}
    >
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{
            width: 56,
            height: 56,
            backgroundColor: isVisuallyDark
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          }}
          aria-hidden="true"
        >
          <i className="las la-flask" style={{ fontSize: "2rem" }} />
        </span>
        <div className="flex-grow-1 min-width-0">
          <div className="font-22 fw-700 mb-1">{t("demoPage.title")}</div>
          <div className="font-14 opacity-90" style={{ lineHeight: 1.55 }}>
            {t("demoPage.intro")}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemoEntryHub — same component as the previous in-profile demo hub.
// Refactored slightly for the standalone page, but visually identical.
// ---------------------------------------------------------------------------
function DemoEntryHub({
  isDarkMode,
  previewPartner,
  clientStateParam,
  onEnterReseller,
  onEnterCustomer,
  onExit,
}) {
  const { t } = useTranslation();
  const isVisuallyDark = !isDarkMode;
  const [openCustomerMenu, setOpenCustomerMenu] = useState(false);
  const menuRef = useRef(null);

  const resellerActive = previewPartner === "approved";
  const customerActive = Boolean(clientStateParam);
  const anyActive = resellerActive || customerActive;

  useEffect(() => {
    if (!openCustomerMenu) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenCustomerMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCustomerMenu]);

  const palette = isVisuallyDark
    ? {
        bg: "linear-gradient(135deg, hsl(263 70% 25% / 0.4), hsl(263 70% 20% / 0.18))",
        border: "1px dashed hsl(263 80% 65%)",
        heading: "hsl(263 85% 88%)",
        body: "hsl(263 35% 78%)",
        badgeBg: "hsl(263 80% 65%)",
        badgeText: "hsl(263 50% 12%)",
        buttonBg: "hsl(263 30% 18%)",
        buttonBorder: "1px solid hsl(263 40% 35%)",
        buttonText: "hsl(263 30% 92%)",
        buttonHoverBg: "hsl(263 35% 24%)",
        iconBg: "hsl(263 70% 60% / 0.22)",
        iconColor: "hsl(263 85% 80%)",
        activeBg:
          "linear-gradient(135deg, hsl(150 70% 35% / 0.32), hsl(150 70% 30% / 0.14))",
        activeBorder: "1px solid hsl(150 70% 55% / 0.55)",
        activeText: "hsl(150 70% 85%)",
        exitBg: "hsl(263 80% 60%)",
        exitText: "#fff",
      }
    : {
        bg: "linear-gradient(135deg, hsl(263 80% 95%), hsl(263 80% 98%))",
        border: "1px dashed hsl(263 60% 60%)",
        heading: "hsl(263 60% 26%)",
        body: "hsl(263 30% 38%)",
        badgeBg: "hsl(263 70% 55%)",
        badgeText: "#fff",
        buttonBg: "#fff",
        buttonBorder: "1px solid hsl(263 30% 85%)",
        buttonText: "hsl(263 50% 28%)",
        buttonHoverBg: "hsl(263 70% 97%)",
        iconBg: "hsl(263 70% 55% / 0.14)",
        iconColor: "hsl(263 60% 40%)",
        activeBg:
          "linear-gradient(135deg, hsl(150 70% 92%), hsl(150 70% 96%))",
        activeBorder: "1px solid hsl(150 60% 50% / 0.45)",
        activeText: "hsl(150 70% 22%)",
        exitBg: "hsl(263 60% 50%)",
        exitText: "#fff",
      };

  const stateLabelKey = clientStateParam
    ? {
        "via-link": "viaLink",
        "code-saved": "codeSaved",
        "has-account": "hasAccount",
      }[clientStateParam] || "fresh"
    : "fresh";

  return (
    <div
      className="rounded-4 p-3 p-md-4 position-relative"
      style={{
        background: palette.bg,
        border: palette.border,
      }}
    >
      <div className="d-flex align-items-start gap-3 flex-wrap">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            backgroundColor: palette.iconBg,
            color: palette.iconColor,
          }}
          aria-hidden="true"
        >
          <i className="las la-flask" style={{ fontSize: "1.5rem" }} />
        </span>
        <div className="flex-grow-1 min-width-0">
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            <span
              className="font-18 fw-700"
              style={{ color: palette.heading }}
            >
              {t("demoHub.title")}
            </span>
            <span
              className="badge fw-700 px-2 py-1 rounded-pill"
              style={{
                backgroundColor: palette.badgeBg,
                color: palette.badgeText,
                fontSize: 10,
                letterSpacing: "0.08em",
              }}
            >
              {t("demoHub.badge")}
            </span>
          </div>
          <p
            className="mb-0 font-14"
            style={{ color: palette.body, lineHeight: 1.55 }}
          >
            {t("demoHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="row gy-2 mt-3">
        <div className="col-md-6">
          <button
            type="button"
            onClick={onEnterReseller}
            className="w-100 d-flex align-items-center gap-3 rounded-3 px-3 py-3 text-start"
            style={{
              backgroundColor: resellerActive
                ? palette.activeBg
                : palette.buttonBg,
              border: resellerActive
                ? palette.activeBorder
                : palette.buttonBorder,
              color: resellerActive ? palette.activeText : palette.buttonText,
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => {
              if (!resellerActive) {
                e.currentTarget.style.backgroundColor = palette.buttonHoverBg;
              }
            }}
            onMouseLeave={(e) => {
              if (!resellerActive) {
                e.currentTarget.style.backgroundColor = palette.buttonBg;
              }
            }}
          >
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                backgroundColor: palette.iconBg,
                color: palette.iconColor,
              }}
              aria-hidden="true"
            >
              <i className="las la-handshake" style={{ fontSize: "1.3rem" }} />
            </span>
            <span className="flex-grow-1 min-width-0">
              <span className="d-block fw-700 font-15">
                {t("demoHub.resellerBtn.title")}
                {resellerActive && (
                  <i
                    className="las la-check-circle ms-2"
                    style={{ color: palette.activeText }}
                    aria-hidden="true"
                  />
                )}
              </span>
              <span
                className="d-block font-12 opacity-85"
                style={{ lineHeight: 1.4 }}
              >
                {t("demoHub.resellerBtn.hint")}
              </span>
            </span>
          </button>
        </div>

        <div className="col-md-6">
          <div ref={menuRef} className="position-relative">
            <button
              type="button"
              onClick={() => setOpenCustomerMenu((v) => !v)}
              className="w-100 d-flex align-items-center gap-3 rounded-3 px-3 py-3 text-start"
              style={{
                backgroundColor: customerActive
                  ? palette.activeBg
                  : palette.buttonBg,
                border: customerActive
                  ? palette.activeBorder
                  : palette.buttonBorder,
                color: customerActive
                  ? palette.activeText
                  : palette.buttonText,
                transition: "all 0.18s ease",
              }}
              onMouseEnter={(e) => {
                if (!customerActive) {
                  e.currentTarget.style.backgroundColor = palette.buttonHoverBg;
                }
              }}
              onMouseLeave={(e) => {
                if (!customerActive) {
                  e.currentTarget.style.backgroundColor = palette.buttonBg;
                }
              }}
              aria-expanded={openCustomerMenu}
              aria-haspopup="true"
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: palette.iconBg,
                  color: palette.iconColor,
                }}
                aria-hidden="true"
              >
                <i
                  className="las la-user-friends"
                  style={{ fontSize: "1.3rem" }}
                />
              </span>
              <span className="flex-grow-1 min-width-0">
                <span className="d-block fw-700 font-15">
                  {t("demoHub.customerBtn.title")}
                  {customerActive && (
                    <i
                      className="las la-check-circle ms-2"
                      style={{ color: palette.activeText }}
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span
                  className="d-block font-12 opacity-85"
                  style={{ lineHeight: 1.4 }}
                >
                  {t("demoHub.customerBtn.hint")}
                </span>
              </span>
              <i
                className={`las ${
                  openCustomerMenu ? "la-angle-up" : "la-angle-down"
                } flex-shrink-0`}
                style={{ fontSize: "1rem", opacity: 0.7 }}
                aria-hidden="true"
              />
            </button>

            {openCustomerMenu && (
              <div
                role="menu"
                className="position-absolute rounded-3 shadow-lg p-2"
                style={{
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  zIndex: 30,
                  backgroundColor: isVisuallyDark
                    ? "hsl(234 28% 16%)"
                    : "#fff",
                  border: isVisuallyDark
                    ? "1px solid hsl(234 25% 26%)"
                    : "1px solid hsl(220 14% 90%)",
                }}
              >
                <div
                  className="px-2 pb-1 fw-700 text-uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    color: isVisuallyDark
                      ? "hsl(234 25% 70%)"
                      : "hsl(220 14% 45%)",
                  }}
                >
                  {t("demoHub.customerStates.title")}
                </div>
                {[
                  { id: "fresh", icon: "la-leaf" },
                  { id: "via-link", icon: "la-link" },
                  { id: "code-saved", icon: "la-bookmark" },
                  { id: "has-account", icon: "la-id-card-alt" },
                ].map((opt) => {
                  const key = {
                    fresh: "fresh",
                    "via-link": "viaLink",
                    "code-saved": "codeSaved",
                    "has-account": "hasAccount",
                  }[opt.id];
                  const selected =
                    (clientStateParam || "fresh") === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onEnterCustomer(opt.id);
                        setOpenCustomerMenu(false);
                      }}
                      className="w-100 d-flex align-items-center gap-2 px-2 py-2 rounded-2 text-start border-0"
                      style={{
                        background: selected
                          ? isVisuallyDark
                            ? "hsl(263 50% 30% / 0.5)"
                            : "hsl(263 70% 95%)"
                          : "transparent",
                        color: isVisuallyDark
                          ? "hsl(234 25% 88%)"
                          : "hsl(220 30% 20%)",
                        transition: "background-color 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected) {
                          e.currentTarget.style.background = isVisuallyDark
                            ? "hsl(234 28% 22%)"
                            : "hsl(220 14% 96%)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      <i
                        className={`las ${opt.icon}`}
                        style={{
                          fontSize: "1.1rem",
                          color: palette.iconColor,
                        }}
                        aria-hidden="true"
                      />
                      <span className="flex-grow-1 font-13 fw-600">
                        {t(`demoHub.customerStates.${key}`)}
                      </span>
                      {selected && (
                        <i
                          className="las la-check"
                          style={{
                            fontSize: "1rem",
                            color: palette.iconColor,
                          }}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {anyActive && (
        <div
          className="mt-3 rounded-3 p-2 d-flex flex-wrap align-items-center justify-content-between gap-2"
          style={{
            background: palette.activeBg,
            border: palette.activeBorder,
          }}
        >
          <div
            className="d-flex align-items-center gap-2 px-2 font-13 fw-600"
            style={{ color: palette.activeText }}
          >
            <i className="las la-flask" aria-hidden="true" />
            {resellerActive
              ? t("demoHub.active.reseller")
              : t("demoHub.active.customer", {
                  state: t(`demoHub.customerStates.${stateLabelKey}`),
                })}
          </div>
          <button
            type="button"
            onClick={onExit}
            className="btn btn-sm pill fw-600 px-3"
            style={{
              backgroundColor: palette.exitBg,
              color: palette.exitText,
              border: "none",
            }}
          >
            <i className="las la-times-circle me-1" aria-hidden="true" />
            {t("demoHub.active.exit")}
          </button>
        </div>
      )}

      <div
        className="mt-3 font-12"
        style={{ color: palette.body, opacity: 0.85, lineHeight: 1.5 }}
      >
        {t("demoHub.footnote")}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemoPlaceholder — empty-state shown when no demo has been picked yet.
// ---------------------------------------------------------------------------
function DemoPlaceholder({ isDarkMode }) {
  const { t } = useTranslation();
  const isVisuallyDark = !isDarkMode;
  return (
    <div
      className="rounded-4 p-4 text-center"
      style={{
        background: isVisuallyDark
          ? "hsl(234 28% 14%)"
          : "hsl(210 14% 98%)",
        border: isVisuallyDark
          ? "1px dashed hsl(234 22% 30%)"
          : "1px dashed hsl(220 14% 82%)",
        color: isVisuallyDark
          ? "hsl(234 18% 75%)"
          : "hsl(220 14% 35%)",
      }}
    >
      <div
        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
        style={{
          width: 64,
          height: 64,
          backgroundColor: isVisuallyDark
            ? "hsl(263 50% 30%)"
            : "hsl(263 60% 92%)",
          color: isVisuallyDark
            ? "hsl(263 85% 80%)"
            : "hsl(263 60% 40%)",
        }}
        aria-hidden="true"
      >
        <i className="las la-hand-point-up" style={{ fontSize: "2rem" }} />
      </div>
      <div className="font-18 fw-700 mb-1">
        {t("demoPage.placeholder.title")}
      </div>
      <div className="font-14" style={{ lineHeight: 1.55, opacity: 0.85 }}>
        {t("demoPage.placeholder.subtitle")}
      </div>
    </div>
  );
}

export default DemoPage;
